import type { Nullable } from 'types';
import {
    FinalCounterpartyType,
    RequisiteAgreementType,
    RequisiteError,
    RequisiteLegalEntityTypeValue,
    RequisiteName,
    RequisiteRole,
    RequisitesAgreementType,
    RequisitesFieldName,
    RequisitesFormName,
} from 'utils/constants/requisites';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

type IsRequisitesFilled = boolean;
type IsRequisitesFormOpen = boolean;
type IsWrongRequisitesModalOpen = boolean;

export type RequisitesErrors = Partial<Record<string, RequisiteError | null>>;

export interface Requisites {
    [RequisiteName.LegalEntityType]: RequisiteLegalEntityTypeValue | null;
    [RequisiteName.LegalName]: string | null;
    [RequisiteName.Inn]: string | null;
    [RequisiteName.AgreementId]?: string | null;
    [RequisiteName.AgreementDate]?: string | null;
    [RequisiteName.AgreementType]?: RequisitesAgreementType | null;
}

export type RequisitesAgency = Omit<Requisites, 'agreementType'>;

export type RequisitesPrimaryAgency = Omit<Requisites, 'agreementType' | 'agreementId' | 'agreementDate'>;

export interface RequisitesForm {
    [RequisitesFormName.FinalAdvertiserRequisites]: Requisites;
    [RequisitesFormName.AgencyRequisites]: RequisitesAgency | null;
    [RequisitesFormName.PrimaryAgencyRequisites]: RequisitesPrimaryAgency | null;
    [RequisitesFieldName.IsAgency]: RequisiteRole | null;
    [RequisitesFieldName.IsAgreementDirect]: RequisiteAgreementType | null;
    [RequisitesFieldName.Consent]: boolean;
    [RequisitesFieldName.FinalCounterpartyType]: FinalCounterpartyType | null;
}

export interface RequisitesPayer {
    [RequisiteName.LegalEntityType]: RequisiteLegalEntityTypeValue | null;
    [RequisiteName.LegalName]: string | null;
    [RequisiteName.Inn]: string | null;
    [RequisiteName.AgreementId]: string | null;
    [RequisiteName.AgreementDate]: string | null;
    [RequisitesFieldName.FinalCounterpartyType]: FinalCounterpartyType | null;
}

interface Warning {
    payerId: number;
    payerName: string;
    invoiceNumber?: number;
    paymentDate?: string;
}

export type RequisitesWarning = Nullable<Warning>;

interface RequisitesFormFieldByName {
    requisiteFormName: RequisitesFormName;
    fieldName: keyof Requisites;
    value: string | number | boolean | null;
}

interface RequisiteFormByName {
    fieldName: keyof RequisitesForm;
    value: RequisitesForm[keyof RequisitesForm];
}

interface RequisiteByName {
    fieldName: RequisitesFieldName;
    value: string | number | boolean | null;
}

interface RequisiteErrorByFieldPath {
    fieldPath: string;
    value: RequisiteError | null;
}

interface RequisitesFormOpenWithParams {
    title?: string;
    buttonName?: string;
    callback?: () => void;
}

export interface RequisitesPayerPayload {
    requisiteFormName: RequisitesFormName;
    payer: RequisitesPayer;
}

// ----------------------- actions -------------------------------------
const SET_ALL_REQUISITES = 'SET_ALL_REQUISITES';
const SET_REQUISITES_FROM_PAYER = 'SET_REQUISITES_FROM_PAYER';
const SET_REQUISITES_PAYERS = 'SET_REQUISITES_PAYERS';
const SET_REQUISITES_FORM_FIELD_BY_NAME = 'SET_REQUISITES_FORM_FIELD_BY_NAME';
const SET_REQUISITE_BY_NAME = 'SET_REQUISITE_BY_NAME';
const SET_INITIAL_IN_REQUISITES_FORM = 'SET_INITIAL_IN_REQUISITES_FORM';
const CLEAR_REQUISITES_FORM = 'CLEAR_REQUISITES_FORM';
const SET_REQUISITE_FORM_BY_NAME = 'SET_REQUISITE_FORM_BY_NAME';
const SET_ALL_REQUISITES_ERRORS = 'SET_ALL_REQUISITES_ERRORS';
const SET_REQUISITE_ERROR_BY_FIELD_PATH = 'SET_REQUISITE_ERROR_BY_FIELD_PATH';
const SET_REQUISITES_FILLED = 'SET_REQUISITES_FILLED';
const SET_REQUISITES_FORM_OPEN = 'SET_REQUISITES_FORM_OPEN';
const SET_WRONG_REQUISITES_MODAL_OPEN = 'SET_WRONG_REQUISITES_MODAL_OPEN';
const SET_REQUISITES_FORM_OPEN_WITH_PARAMS = 'SET_REQUISITES_FORM_OPEN_WITH_PARAMS';
const SET_REQUISITES_WARNING = 'SET_REQUISITES_WARNING';
const RESET_REQUISITES = 'RESET_REQUISITES';

interface PayloadTypes {
    [SET_ALL_REQUISITES]: RequisitesForm;
    [SET_REQUISITES_FROM_PAYER]: RequisitesPayerPayload;
    [SET_REQUISITES_PAYERS]: RequisitesPayer[];
    [SET_REQUISITES_FORM_FIELD_BY_NAME]: RequisitesFormFieldByName;
    [SET_REQUISITE_BY_NAME]: RequisiteByName;
    [SET_INITIAL_IN_REQUISITES_FORM]: RequisitesFormName;
    [CLEAR_REQUISITES_FORM]: RequisitesFormName;
    [SET_REQUISITE_FORM_BY_NAME]: RequisiteFormByName;
    [SET_ALL_REQUISITES_ERRORS]: RequisitesErrors;
    [SET_REQUISITE_ERROR_BY_FIELD_PATH]: RequisiteErrorByFieldPath;
    [SET_REQUISITES_FILLED]: IsRequisitesFilled;
    [SET_REQUISITES_FORM_OPEN]: IsRequisitesFormOpen;
    [SET_WRONG_REQUISITES_MODAL_OPEN]: IsWrongRequisitesModalOpen;
    [SET_REQUISITES_FORM_OPEN_WITH_PARAMS]: RequisitesFormOpenWithParams;
    [SET_REQUISITES_WARNING]: RequisitesWarning;
    [RESET_REQUISITES]: null;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();
export const setAllRequisites = ActionCreator(SET_ALL_REQUISITES);
export const setRequisitesFromPayer = ActionCreator(SET_REQUISITES_FROM_PAYER);
export const setRequisitesPayers = ActionCreator(SET_REQUISITES_PAYERS);
export const setRequisitesFormFieldByName = ActionCreator(SET_REQUISITES_FORM_FIELD_BY_NAME);
export const setRequisiteByName = ActionCreator(SET_REQUISITE_BY_NAME);
export const setInitialInRequisitesForm = ActionCreator(SET_INITIAL_IN_REQUISITES_FORM);
export const clearRequisitesForm = ActionCreator(CLEAR_REQUISITES_FORM);
export const setAllRequisitesErrors = ActionCreator(SET_ALL_REQUISITES_ERRORS);
export const setRequisiteErrorByFieldPath = ActionCreator(SET_REQUISITE_ERROR_BY_FIELD_PATH);
export const setRequisitesFilled = ActionCreator(SET_REQUISITES_FILLED);
export const setRequisitesFormOpen = ActionCreator(SET_REQUISITES_FORM_OPEN);
export const setWrongRequisitesModalOpen = ActionCreator(SET_WRONG_REQUISITES_MODAL_OPEN);
export const setRequisitesFormOpenWithParams = ActionCreator(SET_REQUISITES_FORM_OPEN_WITH_PARAMS);
export const setRequisitesWarning = ActionCreator(SET_REQUISITES_WARNING);
export const resetRequisites = ActionCreator(RESET_REQUISITES);
export const setRequisitesFormByName = ActionCreator(SET_REQUISITE_FORM_BY_NAME);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface RequisitesState {
    requisites: RequisitesForm;
    requisitesPayers: RequisitesPayer[];
    requisitesErrors: RequisitesErrors;
    isRequisitesFilled: IsRequisitesFilled;
    isRequisitesFormOpen: IsRequisitesFormOpen;
    isWrongRequisitesModalOpen: IsWrongRequisitesModalOpen;
    requisitesWarning: RequisitesWarning;
    formParams: RequisitesFormOpenWithParams | null;
}

export const INITIAL_REQUISITES: Record<RequisitesFormName, Requisites> = {
    [RequisitesFormName.FinalAdvertiserRequisites]: {
        [RequisiteName.LegalEntityType]: null,
        [RequisiteName.LegalName]: null,
        [RequisiteName.Inn]: null,
        [RequisiteName.AgreementId]: null,
        [RequisiteName.AgreementDate]: null,
        [RequisiteName.AgreementType]: null,
    },
    [RequisitesFormName.AgencyRequisites]: {
        [RequisiteName.LegalEntityType]: null,
        [RequisiteName.LegalName]: null,
        [RequisiteName.Inn]: null,
        [RequisiteName.AgreementId]: null,
        [RequisiteName.AgreementDate]: null,
    },
    [RequisitesFormName.PrimaryAgencyRequisites]: {
        [RequisiteName.LegalEntityType]: null,
        [RequisiteName.LegalName]: null,
        [RequisiteName.Inn]: null,
    },
};

const REQUISITES_INITIAL_STATE: RequisitesState = {
    requisites: {
        [RequisitesFormName.FinalAdvertiserRequisites]: {
            ...INITIAL_REQUISITES[RequisitesFormName.FinalAdvertiserRequisites],
        },
        [RequisitesFormName.AgencyRequisites]: null,
        [RequisitesFormName.PrimaryAgencyRequisites]: null,
        [RequisitesFieldName.IsAgency]: null,
        [RequisitesFieldName.IsAgreementDirect]: null,
        [RequisitesFieldName.Consent]: false,
        [RequisitesFieldName.FinalCounterpartyType]: null,
    },
    requisitesPayers: [],
    requisitesErrors: {},
    isRequisitesFilled: false,
    isRequisitesFormOpen: false,
    isWrongRequisitesModalOpen: false,
    requisitesWarning: null,
    formParams: null,
};

export default createReducer<RequisitesState, PayloadTypes>(REQUISITES_INITIAL_STATE, {
    [SET_ALL_REQUISITES]: (state, { payload }) => ({
        ...state,
        requisites: payload,
    }),
    [SET_REQUISITES_FROM_PAYER]: (state, { payload }) => {
        const { requisites } = state;
        const requisiteForm = requisites[payload.requisiteFormName];

        const { finalCounterpartyType, ...payerWithoutFinalCounterpartyType } = payload.payer;

        return {
            ...state,
            requisites: {
                ...requisites,
                [payload.requisiteFormName]: {
                    ...requisiteForm,
                    ...payerWithoutFinalCounterpartyType,
                },
                finalCounterpartyType: payload.payer.finalCounterpartyType,
            },
        };
    },
    [SET_REQUISITES_PAYERS]: (state, { payload }) => ({
        ...state,
        requisitesPayers: payload,
    }),
    [SET_REQUISITES_FORM_FIELD_BY_NAME]: (state, { payload }) => {
        const { requisites } = state;
        const requisiteForm = requisites[payload.requisiteFormName];
        return {
            ...state,
            requisites: {
                ...requisites,
                [payload.requisiteFormName]: {
                    ...requisiteForm,
                    [payload.fieldName]: payload.value,
                },
            },
        };
    },
    [SET_REQUISITE_BY_NAME]: (state, { payload }) => ({
        ...state,
        requisites: {
            ...state.requisites,
            [payload.fieldName]: payload.value,
        },
    }),
    [SET_REQUISITE_FORM_BY_NAME]: (state, { payload }) => ({
        ...state,
        requisites: {
            ...state.requisites,
            [payload.fieldName]: payload.value,
        },
    }),
    [SET_INITIAL_IN_REQUISITES_FORM]: (state, { payload }) => ({
        ...state,
        requisites: {
            ...state.requisites,
            [payload]: { ...INITIAL_REQUISITES[payload] },
        },
    }),
    [CLEAR_REQUISITES_FORM]: (state, { payload }) => ({
        ...state,
        requisites: {
            ...state.requisites,
            [payload]: null,
        },
    }),
    [SET_ALL_REQUISITES_ERRORS]: (state, { payload }) => ({
        ...state,
        requisitesErrors: payload,
    }),
    [SET_REQUISITE_ERROR_BY_FIELD_PATH]: (state, { payload }) => ({
        ...state,
        requisitesErrors: {
            ...state.requisitesErrors,
            [payload.fieldPath]: payload.value,
        },
    }),
    [SET_REQUISITES_FILLED]: (state, { payload }) => ({
        ...state,
        isRequisitesFilled: payload,
    }),
    [SET_REQUISITES_FORM_OPEN]: (state, { payload }) => ({
        ...state,
        isRequisitesFormOpen: payload,
        formParams: null,
    }),
    [SET_WRONG_REQUISITES_MODAL_OPEN]: (state, { payload }) => ({
        ...state,
        isWrongRequisitesModalOpen: payload,
        formParams: null,
    }),
    [SET_REQUISITES_FORM_OPEN_WITH_PARAMS]: (state, { payload }) => ({
        ...state,
        isRequisitesFormOpen: true,
        formParams: payload,
    }),
    [SET_REQUISITES_WARNING]: (state, { payload }) => ({
        ...state,
        requisitesWarning: payload,
    }),
    [RESET_REQUISITES]: (state) => ({
        ...state,
        requisites: { ...REQUISITES_INITIAL_STATE.requisites },
    }),
});
// ----------------------- /reducer --------------------------------------
